import { h, render } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import Router from 'preact-router';
import { authState, loadSession } from './state/auth.js';
import { loadTheme } from './state/theme.js';
import { connectSocket, loadNotifications } from './state/notifications.js';
import { route } from 'preact-router';
import { localeReady, locale } from './i18n.js';
import { updateCurrentPath } from './state/ui.js';

// Views
import LoginView from './views/LoginView.jsx';
import ForgotPasswordView from './views/ForgotPasswordView.jsx';
import ResetPasswordView from './views/ResetPasswordView.jsx';
import ClubSelectView from './views/ClubSelectView.jsx';
import DashboardView from './views/DashboardView.jsx';
import ProcessListView from './views/ProcessListView.jsx';
import ProcessDetailView from './views/ProcessDetailView.jsx';
import TaskDetailView from './views/TaskDetailView.jsx';
import TasksView from './views/TasksView.jsx';
import DocumentsView from './views/DocumentsView.jsx';
import TemplatesView from './views/TemplatesView.jsx';
import UsersView from './views/UsersView.jsx';
import NotificationsView from './views/NotificationsView.jsx';
import AuditView from './views/AuditView.jsx';
import ReportsView from './views/ReportsView.jsx';
import OrgChartView from './views/OrgChartView.jsx';
import PolicyLibraryView from './views/PolicyLibraryView.jsx';
import ClubSettingsView from './views/ClubSettingsView.jsx';
import PlatformAdminView from './views/PlatformAdminView.jsx';
import SectionsView from './views/SectionsView.jsx';
import RolesView from './views/RolesView.jsx';
import NewsView from './views/NewsView.jsx';
import ProfileView from './views/ProfileView.jsx';
import CoinsView from './views/CoinsView.jsx';
import RecurringView from './views/RecurringView.jsx';
import SeasonsView from './views/SeasonsView.jsx';
import SportlinkView from './views/SportlinkView.jsx';
import MarketplaceView from './views/MarketplaceView.jsx';
import HelpView from './views/HelpView.jsx';
import SetupView from './views/SetupView.jsx';
import CalendarView from './views/CalendarView.jsx';
import JaarplanningView from './views/JaarplanningView.jsx';
import MeetingsView, { MeetingBodyView, DecisionsView } from './views/MeetingsView.jsx';
import VolunteersView from './views/VolunteersView.jsx';
import GoalsView from './views/GoalsView.jsx';
import StoringenView from './views/StoringenView.jsx';
import ComplexClubsView from './views/ComplexClubsView.jsx';
import ComplexOverlegView from './views/ComplexOverlegView.jsx';
import MaintenanceView from './views/MaintenanceView.jsx';
import ComplexReportView from './views/ComplexReportView.jsx';
import ClubtrotsView from './views/ClubtrotsView.jsx';
import HandoverView from './views/HandoverView.jsx';
import WelcomeView from './views/WelcomeView.jsx';
import SearchView from './views/SearchView.jsx';
import MeetingDetailView from './views/MeetingDetailView.jsx';
import CertificationView from './views/CertificationView.jsx';
import KwalificatiesView from './views/KwalificatiesView.jsx';
import PlanLockedView from './views/PlanLockedView.jsx';
import { plan, loadPlan, lockOf } from './utils/plan.js';

// Layout
import Shell from './components/layout/Shell.jsx';

// CSS
import './app.css';

// The active environment is a sportcomplex (punt 4)
const isComplexEnv = ({ clubs, activeClubId }) => clubs?.find(c => Number(c.id) === Number(activeClubId))?.kind === 'sportcomplex';

function handleRoute(e) {
  // Why: Update the path signal so Sidebar active state re-renders
  updateCurrentPath();
  const { authenticated, isPlatformAdmin, activeClubId, loading } = authState.value;
  const path = e.url;

  // Why: Don't redirect while session is still loading
  if (loading) return;

  // Unauthenticated users can only access login and forgot-password
  if (!authenticated && path !== '/login' && path !== '/forgot-password' && !path?.startsWith('/reset-password')) {
    route('/login', true);
    return;
  }

  // Authenticated user without active club → club/env selector (platform admins can also go to /admin)
  if (authenticated && !activeClubId && path !== '/select-club' && path !== '/admin' && path !== '/login') {
    route('/select-club', true);
    return;
  }

  // A sportcomplex has no club dashboard: its staff start at the reports
  if (authenticated && path === '/dashboard' && isComplexEnv(authState.value)) {
    route('/storingen', true);
    return;
  }

  // Redirect from login if already authenticated
  if (authenticated && path === '/login') {
    if (activeClubId) route('/dashboard', true);
    else route('/select-club', true);
    return;
  }
}

function App() {
  // Why: Subscribe to locale changes to force full re-render of all child components
  const [localeKey, setLocaleKey] = useState(locale.value);
  useEffect(() => {
    const unsub = locale.subscribe(v => setLocaleKey(v));
    return unsub;
  }, []);

  useEffect(() => {
    const init = async () => {
      const data = await loadSession();
      if (data) {
        if (data.activeClubId) {
          await loadTheme();
          connectSocket();
          loadNotifications();
          if (window.location.pathname === '/' || window.location.pathname === '/login') {
            route('/dashboard');
          } else if (window.location.pathname === '/dashboard' && isComplexEnv(data)) {
            // Opened straight at /dashboard (reload, bookmark, the installed app)
            route('/storingen', true);
          }
        } else {
          route('/select-club');
        }
      } else {
        const p = window.location.pathname;
        if (p !== '/forgot-password' && !p.startsWith('/reset-password')) {
          route('/login');
        }
      }
    };
    init();
  }, []);

  const { loading, authenticated, isPlatformAdmin } = authState.value;

  // Why: Wait for both auth session check and locale to load before rendering
  if (loading || !localeReady.value) {
    return <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:var(--font-display);color:var(--color-text-muted)">Loading...</div>;
  }

  return (
    <Router key={localeKey} onChange={handleRoute}>
      <LoginView path="/login" />
      <ForgotPasswordView path="/forgot-password" />
      <ResetPasswordView path="/reset-password" />
      <ClubSelectView path="/select-club" />

      {/* Wrapped views with shell */}
      <ShellRoute path="/dashboard" component={DashboardView} />
      <ShellRoute path="/processes" component={ProcessListView} />
      <ShellRoute path="/processes/:id" component={ProcessDetailView} />
      <ShellRoute path="/tasks" component={TasksView} />
      <ShellRoute path="/tasks/:id" component={TaskDetailView} />
      <ShellRoute path="/documents" component={DocumentsView} />
      <ShellRoute path="/templates" component={TemplatesView} />
      <ShellRoute path="/users" component={UsersView} />
      <ShellRoute path="/notifications" component={NotificationsView} />
      <ShellRoute path="/audit" component={AuditView} />
      <ShellRoute path="/reports" module="reports" component={ReportsView} />
      <ShellRoute path="/org-chart" component={OrgChartView} />
      <ShellRoute path="/policies" component={PolicyLibraryView} />
      <ShellRoute path="/settings" component={ClubSettingsView} />
      <ShellRoute path="/sections" component={SectionsView} />
      <ShellRoute path="/roles" component={RolesView} />
      <ShellRoute path="/news" component={NewsView} />
      <ShellRoute path="/profile" component={ProfileView} />
      <ShellRoute path="/profile/:userId" component={ProfileView} />
      <ShellRoute path="/coins" module="coins" component={CoinsView} />
      <ShellRoute path="/recurring" module="recurring" component={RecurringView} />
      <ShellRoute path="/seasons" component={SeasonsView} />
      <ShellRoute path="/meetings" module="meetings" component={MeetingsView} />
      <ShellRoute path="/decisions" module="meetings" component={DecisionsView} />
      <ShellRoute path="/vrijwilligers" module="volunteers" component={VolunteersView} />
      <ShellRoute path="/goals" module="goals" component={GoalsView} />
      <ShellRoute path="/storingen" module="facilities" component={StoringenView} />
      <ShellRoute path="/storingen/:id" module="facilities" component={StoringenView} />
      <ShellRoute path="/complex/clubs" component={ComplexClubsView} />
      <ShellRoute path="/complex/overleg" component={ComplexOverlegView} />
      <ShellRoute path="/complex/overleg/:id" component={ComplexOverlegView} />
      <ShellRoute path="/onderhoud" component={MaintenanceView} />
      <ShellRoute path="/onderhoud/:id" component={MaintenanceView} />
      <ShellRoute path="/complex/rapportage" component={ComplexReportView} />
      <ShellRoute path="/clubtrots" module="pride" component={ClubtrotsView} />
      <ShellRoute path="/overdracht/:id" module="handovers" component={HandoverView} />
      <ShellRoute path="/welkom/:id" module="welcome" component={WelcomeView} />
      <ShellRoute path="/search" component={SearchView} />
      <ShellRoute path="/meetings/bodies/:id" module="meetings" component={MeetingBodyView} />
      <ShellRoute path="/meetings/:id" module="meetings" component={MeetingDetailView} />
      <ShellRoute path="/sportlink" module="sportlink" component={SportlinkView} />
      <ShellRoute path="/marketplace" module="marketplace" component={MarketplaceView} />
      <ShellRoute path="/help" component={HelpView} />
      <ShellRoute path="/inrichten" component={SetupView} />
      <ShellRoute path="/inrichten/:step" component={SetupView} />
      <ShellRoute path="/calendar" component={CalendarView} />
      <ShellRoute path="/jaarplanning" component={JaarplanningView} />
      <ShellRoute path="/certification" module="certification" component={CertificationView} />
      <ShellRoute path="/kwalificaties" module="kwalificaties" component={KwalificatiesView} />
      <ShellRoute path="/admin" component={PlatformAdminView} />

      {/* Default redirect */}
      <Redirect path="/" to="/login" default />
    </Router>
  );
}

// Why: Wraps a view with the Shell layout (sidebar + header)
function ShellRoute({ component: Component, module, ...props }) {
  return (
    <Shell>
      {module ? <PlanGate module={module}><Component {...props} /></PlanGate> : <Component {...props} />}
    </Shell>
  );
}

// A module outside the club's plan shows what it offers instead of the page
function PlanGate({ module, children }) {
  const { userId, activeClubId } = authState.value;
  const key = `${userId}:${activeClubId}`;
  useEffect(() => { loadPlan(key); }, [key]);
  if (plan.value?.key !== key) return null;
  const lock = lockOf(module);
  return lock ? <PlanLockedView lock={lock} /> : children;
}

function Redirect({ to }) {
  useEffect(() => { route(to, true); }, []);
  return null;
}

render(<App />, document.getElementById('app'));
